import React, { useState, useEffect } from 'react';
import { Lock, Eye, Globe, UserX, Download } from 'lucide-react';
import api from '../../../api';
import './AccountSections.css';

const PrivacySection = ({ userId }) => {
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    allowMessages: true,
    dataSharing: false,
    marketingEmails: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPrivacySettings();
  }, [userId]);

  const fetchPrivacySettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/brands/${userId}/privacy/settings`);

      if (response.data) {
        setPrivacySettings({
          profileVisibility: response.data.profileVisibility || 'public',
          showEmail: response.data.showEmail ?? false,
          showPhone: response.data.showPhone ?? false,
          allowMessages: response.data.allowMessages ?? true,
          dataSharing: response.data.dataSharing ?? false,
          marketingEmails: response.data.marketingEmails ?? false
        });
      }
    } catch (err) {

      let errorMessage = 'Failed to load privacy settings';
      if (err.response) {
        if (err.response.status === 403) {
          errorMessage = 'Access denied. Please log in again.';
        } else if (err.response.status === 404) {
          errorMessage = err.response.data?.message || 'User not found';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPrivacySettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleVisibilityChange = (value) => {
    setPrivacySettings(prev => ({
      ...prev,
      profileVisibility: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const response = await api.post(`/brands/${userId}/privacy/settings`, privacySettings);
      setMessage({ type: 'success', text: 'Privacy settings saved successfully!' });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {

      let errorMessage = 'Failed to save privacy settings';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadData = async () => {
    try {
      setMessage({ type: '', text: '' });

      const response = await api.get(`/brands/${userId}/account/download-data`);
      setMessage({
        type: 'success',
        text: response.data.message || 'Your data download will be ready shortly. You will receive an email with the download link.'
      });

      // Clear success message after 10 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 10000);

    } catch (error) {
      let errorMessage = 'Failed to request data download. Please try again later.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data.')) {
      return;
    }

    // Double confirmation
    if (!window.confirm('This is your final warning. Are you absolutely sure you want to permanently delete your account?')) {
      return;
    }

    try {
      setMessage({ type: '', text: '' });
      const response = await api.delete(`/brands/${userId}/account`);
      // Show success message
      setMessage({
        type: 'success',
        text: 'Account deleted successfully. Redirecting...'
      });

      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Redirect to home page after 2 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);

    } catch (error) {
      let errorMessage = 'Failed to delete account. Please try again or contact support.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">Privacy Settings</h2>
        <p className="section-description">Control your privacy and data preferences</p>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading privacy settings...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
        </div>
      )}

      {message.text && (
        <div className={`message-state ${message.type}`}>
          {message.text}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Profile Visibility */}
          <div className="settings-section">
        <h3 className="settings-section-title">
          <Eye size={20} className="inline mr-2" />
          Profile Visibility
        </h3>

        <div className="visibility-options">
          <label className="radio-option">
            <input
              type="radio"
              name="visibility"
              value="public"
              checked={privacySettings.profileVisibility === 'public'}
              onChange={(e) => handleVisibilityChange(e.target.value)}
            />
            <div className="radio-content">
              <Globe size={20} className="text-blue-600" />
              <div>
                <p className="radio-label">Public</p>
                <p className="radio-description">Anyone can view your profile</p>
              </div>
            </div>
          </label>

          <label className="radio-option">
            <input
              type="radio"
              name="visibility"
              value="private"
              checked={privacySettings.profileVisibility === 'private'}
              onChange={(e) => handleVisibilityChange(e.target.value)}
            />
            <div className="radio-content">
              <Lock size={20} className="text-orange-600" />
              <div>
                <p className="radio-label">Private</p>
                <p className="radio-description">Only you can view your profile</p>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Contact Information */}
      <div className="settings-section">
        <h3 className="settings-section-title">
          <Lock size={20} className="inline mr-2" />
          Contact Information
        </h3>

        <div className="settings-list">
          <div className="setting-item">
            <div className="setting-info">
              <div>
                <p className="setting-label">Show Email Address</p>
                <p className="setting-description">Allow others to see your email</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={privacySettings.showEmail}
                onChange={() => handleToggle('showEmail')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div>
                <p className="setting-label">Show Phone Number</p>
                <p className="setting-description">Allow others to see your phone number</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={privacySettings.showPhone}
                onChange={() => handleToggle('showPhone')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div>
                <p className="setting-label">Allow Direct Messages</p>
                <p className="setting-description">Let creators send you messages</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={privacySettings.allowMessages}
                onChange={() => handleToggle('allowMessages')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* Data & Privacy */}
      <div className="settings-section">
        <h3 className="settings-section-title">
          <Lock size={20} className="inline mr-2" />
          Data & Marketing
        </h3>

        <div className="settings-list">
          <div className="setting-item">
            <div className="setting-info">
              <div>
                <p className="setting-label">Data Sharing</p>
                <p className="setting-description">Share anonymized data for analytics</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={privacySettings.dataSharing}
                onChange={() => handleToggle('dataSharing')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div>
                <p className="setting-label">Marketing Emails</p>
                <p className="setting-description">Receive promotional emails</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={privacySettings.marketingEmails}
                onChange={() => handleToggle('marketingEmails')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="settings-section">
        <h3 className="settings-section-title">
          <Download size={20} className="inline mr-2" />
          Data Management
        </h3>

        <div className="data-actions">
          <button onClick={handleDownloadData} className="btn-outline">
            <Download size={18} className="inline mr-2" />
            Download My Data
          </button>
          <p className="text-sm text-gray-600">Get a copy of your account data</p>
        </div>
      </div>

      {/* Delete Account */}
      <div className="settings-section">
        <h3 className="settings-section-title text-red-600">
          <UserX size={20} className="inline mr-2" />
          Delete Account
        </h3>

        <div className="danger-zone">
          <p className="danger-warning">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <button onClick={handleDeleteAccount} className="btn-danger">
            Delete My Account
          </button>
        </div>
      </div>

      <div className="form-actions">
        <button onClick={handleSave} className="btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Privacy Settings'}
        </button>
      </div>
        </>
      )}
    </div>
  );
};

export default PrivacySection;


